import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

/**
 * ServiceItemForm — Phase 1 "⚙ Service" form.
 * Creates an order-item with `source_type: "service"`. Price is entered
 * manually by the user (no predefined pricing).
 */
export default function ServiceItemForm({ open, onClose, onAdd }) {
  const { t } = useI18n();
  const [types, setTypes] = useState([]);
  const [serviceType, setServiceType] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unitPrice, setUnitPrice] = useState("0");
  const [unitCost, setUnitCost] = useState("0");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/catalog/services`, {
          withCredentials: true,
        });
        setTypes(res.data || []);
        setServiceType((prev) =>
          !prev && (res.data || []).length ? res.data[0] : prev
        );
      } catch (err) {
        toast.error(t("service_load_error"));
      }
    })();
  }, [open, t]);

  const handleAdd = () => {
    if (!serviceType) {
      toast.error(t("service_form_type_required"));
      return;
    }
    const qty = parseFloat(quantity) || 0;
    if (qty <= 0) {
      toast.error(t("service_form_qty_required"));
      return;
    }
    const price = parseFloat(unitPrice) || 0;
    const cost = parseFloat(unitCost) || 0;
    const item = {
      product_code: "SERVICE",
      description: serviceType,
      quantity: qty,
      notes: notes || "",
      unit_price: price,
      unit_cost: cost,
      // Phase-1 trace fields
      source_type: "service",
      service_type: serviceType,
    };
    onAdd([item]);
    toast.success(t("service_form_added_toast"));
    // Reset for next use
    setQuantity("1");
    setUnitPrice("0");
    setNotes("");
    onClose();
  };

  if (!open) return null;

  return (
    <div
      data-testid="service-form-overlay"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        data-testid="service-form-modal"
        className="bg-white w-full max-w-xl border border-black shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-black flex items-center justify-between">
          <div>
            <div className="label-overline">GP / ADD SERVICE</div>
            <h2 className="font-display text-2xl font-black tracking-tight mt-1">
              {t("service_form_title")}
            </h2>
          </div>
          <button
            type="button"
            data-testid="service-form-close"
            onClick={onClose}
            className="font-mono-tech text-xs font-bold px-3 py-2 border border-black hover:bg-black hover:text-white"
          >
            ✕
          </button>
        </header>

        <div className="p-6 space-y-4">
          <div>
            <label className="label-overline block mb-1">
              {t("service_form_type_label")}
            </label>
            <select
              data-testid="service-form-type"
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value)}
              className="w-full px-3 py-2 border border-black bg-white focus:outline-none"
            >
              {types.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label-overline block mb-1">
                {t("service_form_qty_label")}
              </label>
              <input
                data-testid="service-form-qty"
                type="number"
                min="0"
                step="any"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full px-3 py-2 border border-black bg-white focus:outline-none"
              />
            </div>
            <div>
              <label className="label-overline block mb-1">
                {t("service_form_price_label")}
              </label>
              <input
                data-testid="service-form-price"
                type="number"
                min="0"
                step="0.01"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-black bg-white focus:outline-none"
              />
            </div>
            <div>
              <label className="label-overline block mb-1">
                {t("service_form_cost_label") || "Costo unitario"}
              </label>
              <input
                data-testid="service-form-cost"
                type="number"
                min="0"
                step="0.01"
                value={unitCost}
                onChange={(e) => setUnitCost(e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-black bg-white focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="label-overline block mb-1">
              {t("service_form_notes_label")}
            </label>
            <textarea
              data-testid="service-form-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-black bg-white focus:outline-none text-sm"
              placeholder="—"
            />
          </div>
        </div>

        <footer className="px-6 py-4 border-t border-black flex items-center justify-end gap-2">
          <button
            type="button"
            data-testid="service-form-cancel"
            onClick={onClose}
            className="font-display text-sm font-bold border border-black px-4 py-2 hover:bg-[var(--bg)]"
          >
            {t("cancel")}
          </button>
          <button
            type="button"
            data-testid="service-form-add"
            onClick={handleAdd}
            className="font-display text-sm font-bold bg-black text-white px-4 py-2 hover:bg-[var(--text-secondary)]"
          >
            {t("service_form_add_btn")}
          </button>
        </footer>
      </div>
    </div>
  );
}
